/**
 * The admin app's time module.
 *
 * Same rule as the API's `lib/time.ts`, for the same reason: one place that
 * touches dates, so a service date is derived identically everywhere. This file
 * is the only one in `web/src` exempt from `npm run check:time`.
 *
 * The venue's timezone is used throughout, NEVER the browser's. A manager
 * reviewing Doha covers from London must see Doha's service dates, or the
 * numbers are meaningless.
 */

export interface VenueTime {
  timezone: string;
  serviceDayCutoffHour: number;
  digitStyle?: 'western' | 'arabic';
}

export type ServiceDate = string;

/** Now, as an instant. The one sanctioned reader of wall time in this app. */
export function now(): Date {
  return new Date();
}

function parts(instant: Date, venue: VenueTime) {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: venue.timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const bag: Record<string, string> = {};
  for (const part of formatter.formatToParts(instant)) {
    if (part.type !== 'literal') bag[part.type] = part.value;
  }
  return {
    date: `${bag.year}-${bag.month}-${bag.day}`,
    hour: Number(bag.hour === '24' ? '00' : bag.hour),
    minute: Number(bag.minute),
    clock: `${bag.hour === '24' ? '00' : bag.hour}:${bag.minute}`,
  };
}

export function addServiceDays(date: ServiceDate, days: number): ServiceDate {
  const [y = 0, m = 1, d = 1] = date.split('-').map(Number);
  const utc = new Date(Date.UTC(y, m - 1, d));
  utc.setUTCDate(utc.getUTCDate() + days);
  return utc.toISOString().slice(0, 10);
}

/**
 * The service date an instant belongs to.
 *
 * Before the venue's cutoff hour, an instant belongs to the PREVIOUS service
 * date - so at 01:30 during suhoor the floor screen still shows the evening
 * that is actually in progress, rather than jumping to tomorrow at midnight.
 */
export function serviceDateFor(instant: Date, venue: VenueTime): ServiceDate {
  const p = parts(instant, venue);
  return p.hour < venue.serviceDayCutoffHour ? addServiceDays(p.date, -1) : p.date;
}

export function currentServiceDate(venue: VenueTime): ServiceDate {
  return serviceDateFor(now(), venue);
}

/** Venue-local wall clock for an instant, `HH:mm`. */
export function venueClock(instant: Date | string, venue: VenueTime): string {
  const at = typeof instant === 'string' ? new Date(instant) : instant;
  return parts(at, venue).clock;
}

/**
 * Pin the numbering system explicitly in both directions.
 *
 * Never rely on the locale default: which digits a bare `ar` locale produces
 * depends on the runtime's ICU build. A venue setting that means different
 * things on the server and on the host's tablet is not a setting.
 */
function intlLocale(locale: string, digitStyle: 'western' | 'arabic'): string {
  const numbering = digitStyle === 'arabic' ? 'arab' : 'latn';
  return locale.includes('-u-') ? `${locale}-nu-${numbering}` : `${locale}-u-nu-${numbering}`;
}

export function formatTime(
  instant: Date | string,
  venue: VenueTime,
  locale = 'en',
): string {
  const at = typeof instant === 'string' ? new Date(instant) : instant;
  return new Intl.DateTimeFormat(intlLocale(locale, venue.digitStyle ?? 'western'), {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: venue.timezone,
  }).format(at);
}

export function formatDate(
  instant: Date | string | ServiceDate,
  venue: VenueTime,
  locale = 'en',
): string {
  const at =
    typeof instant === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(instant)
      ? new Date(`${instant}T12:00:00Z`)
      : typeof instant === 'string'
        ? new Date(instant)
        : instant;

  return new Intl.DateTimeFormat(intlLocale(locale, venue.digitStyle ?? 'western'), {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: venue.timezone,
  }).format(at);
}

/**
 * Hijri, Umm al-Qura - the civil calendar in the Gulf.
 *
 * Plain `islamic` is an astronomical variant and can land a day off from the
 * printed Qatari calendar, which is exactly the confusion showing both
 * calendars exists to prevent.
 */
export function formatHijri(
  instant: Date | string | ServiceDate,
  venue: VenueTime,
  locale = 'ar',
): string {
  const at =
    typeof instant === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(instant)
      ? new Date(`${instant}T12:00:00Z`)
      : typeof instant === 'string'
        ? new Date(instant)
        : instant;

  return new Intl.DateTimeFormat(
    intlLocale(`${locale}-u-ca-islamic-umalqura`, venue.digitStyle ?? 'western'),
    { day: 'numeric', month: 'long', year: 'numeric', timeZone: venue.timezone },
  ).format(at);
}

/** `HH:mm` (hours 0-47) to minutes past midnight, for the time scrubber. */
export function clockToMinutes(clock: string): number {
  const [h = '0', m = '0'] = clock.split(':');
  return Number(h) * 60 + Number(m);
}

/** Minutes past midnight to `HH:mm`, wrapping 24+ hours for display. */
export function minutesToClock(minutes: number, forDisplay = false): string {
  const total = Math.max(0, Math.round(minutes));
  const hour = Math.floor(total / 60);
  const minute = total % 60;
  const shown = forDisplay ? hour % 24 : hour;
  return `${String(shown).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

export function minutesBetween(a: Date | string, b: Date | string): number {
  const from = typeof a === 'string' ? new Date(a) : a;
  const to = typeof b === 'string' ? new Date(b) : b;
  return Math.round((to.getTime() - from.getTime()) / 60_000);
}

/** How long ago, in whole minutes. Used for waitlist waits. */
export function minutesSince(instant: Date | string): number {
  return minutesBetween(instant, now());
}
